import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, CreditCard, LogOut, ShieldCheck } from 'lucide-react';
import { WorkspaceRole } from '@mg/shared';
import { Avatar } from '../ui/Avatar';
import { useAuth } from '../../auth/AuthContext';
import { useIsTenantAdmin } from '../../auth/useWorkspaceAdmin';
import { useTenant } from '../../auth/TenantContext';
import { useUserAvatar } from '../../auth/UserAvatarContext';
import { devAuthEnabled } from '../../lib/env';
import { useDismissable } from '../../lib/useDismissable';
import { cn } from '../../lib/cn';
import { menuIcons } from './menuIcons';
import { MenuItem, MenuScrim, chromeFocusRing, chromeMenuPanel } from './MenuItem';
import { useShellMenu } from './useShellMenu';

/** The user's role where the picker points, in the words the Members screen uses. */
function roleLabel(isTenantAdmin: boolean, role: WorkspaceRole | undefined): string | null {
  if (isTenantAdmin) return 'Tenant Admin';
  if (role === WorkspaceRole.WORKSPACE_ADMIN) return 'Brand Workspace Admin';
  return role ? 'Member' : null;
}

/**
 * The account menu at the right of the top bar: the avatar, the name, and under it the active Brand
 * Workspace and the user's role there. The entries and their gates are unchanged.
 *
 * Below `lg` the button is the avatar and the chevron alone, and the bar anchors the menu, under the
 * bar: 12px from the right edge on a phone (300px wide, never wider than the screen less 24px), 16px on
 * a tablet.
 */
export function UserMenu() {
  const { principal, signOut } = useAuth();
  const isTenantAdmin = useIsTenantAdmin();
  // Server-computed from an email allowlist; the client only mirrors it.
  const isPlatformBillingAdmin = Boolean(principal?.user?.isPlatformBillingAdmin);
  const { activeTenant } = useTenant();
  const { photoUrl } = useUserAvatar();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);
  useDismissable(open, ref, close);
  useShellMenu(open, close, ref);

  const name = principal?.user.displayName ?? 'Account';
  const email = principal?.user.email ?? '';
  // The membership is looked up by the workspace the picker shows. NOT `principal.activeTenantId`: the
  // principal is fetched once, and the server sets that to the user's FIRST membership, so it does not
  // follow the picker.
  const membership = principal?.memberships?.find((m) => m.tenantId === activeTenant?.id);
  const workspace = activeTenant?.name;
  const role = roleLabel(isTenantAdmin, membership?.role);
  const detail = [workspace, role].filter(Boolean).join(' · ');

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  return (
    // `ml-auto`: when the bar wraps and this lands on a line of its own, it keeps to the right edge, so
    // the menu, anchored on its right, opens on screen.
    <div className="ml-auto shrink-0 lg:relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          'flex h-10 items-center gap-2 rounded-[0.625rem] pl-1 pr-2 text-left transition-colors hover:bg-chrome-hover',
          // Below `lg`: a 44px target, outlined while its menu is open.
          'max-lg:min-h-[var(--touch-target)] max-lg:aria-expanded:ring-1 max-lg:aria-expanded:ring-inset max-lg:aria-expanded:ring-white/40',
          chromeFocusRing,
        )}
      >
        <Avatar name={name} size="sm" fetchCustom={false} photoUrl={photoUrl} tone="chrome" />
        {/* Below `lg` only the avatar shows: this names the button there. */}
        <span className="sr-only lg:hidden">Account: {name}</span>
        <span className="hidden min-w-0 lg:block">
          <span className="block max-w-[10rem] truncate text-[0.8125rem] font-semibold leading-5 text-chrome-text">
            {name}
          </span>
          {/* A long workspace name is cut, never the role after it: the role shows nowhere else on the
              bar, and the picker prints the name in full. The whole line is the tooltip. */}
          {detail && (
            <span className="flex max-w-[10rem] text-xs text-chrome-muted" title={detail}>
              {workspace && <span className="truncate">{workspace}</span>}
              {role && <span className="shrink-0 whitespace-pre">{workspace ? ` · ${role}` : role}</span>}
            </span>
          )}
        </span>
        <ChevronDown
          className={cn('h-3.5 w-3.5 text-chrome-muted transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Account menu"
          className={cn(
            'absolute z-20 mt-2 overflow-hidden py-1 lg:right-0 lg:w-60',
            'max-lg:top-full max-lg:w-[18.75rem] max-lg:max-w-[calc(100vw-1.5rem)] max-md:right-3 md:max-lg:right-4',
            chromeMenuPanel,
          )}
        >
          <div className="border-b border-chrome-control-line px-4 py-3">
            <p className="truncate text-sm font-semibold text-chrome-text">{name}</p>
            {email && <p className="truncate text-xs text-chrome-muted">{email}</p>}
          </div>

          {/* Glyphs from menuIcons: the My Profile and App Settings page headers show the same ones. */}
          <MenuItem icon={<menuIcons.profile className="h-4 w-4" />} onClick={() => go('/profile')}>
            My Profile
          </MenuItem>
          <MenuItem
            icon={<menuIcons.appSettings className="h-4 w-4" />}
            onClick={() => go('/app-settings')}
          >
            App Settings
          </MenuItem>
          {isTenantAdmin && (
            <MenuItem
              icon={<menuIcons.siteIntegrations className="h-4 w-4" />}
              onClick={() => go('/site-integrations')}
            >
              Site Integrations
            </MenuItem>
          )}
          {isTenantAdmin && (
            <MenuItem icon={<CreditCard className="h-4 w-4" />} onClick={() => go('/billing')}>
              Billing
            </MenuItem>
          )}
          {/* Billing Admin is allowlisted by email on the server, NOT a role — it decides which
              plans the organisation may buy, so it sits above Tenant Admin. */}
          {isPlatformBillingAdmin && (
            <MenuItem icon={<ShieldCheck className="h-4 w-4" />} onClick={() => go('/billing-admin')}>
              Billing Admin
            </MenuItem>
          )}

          <div className="my-1 border-t border-chrome-control-line" />

          <MenuItem
            icon={<LogOut className="h-4 w-4" />}
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await signOut();
              } finally {
                setBusy(false);
                setOpen(false);
              }
            }}
          >
            {devAuthEnabled ? 'Reset' : 'Sign out'}
          </MenuItem>
        </div>
      )}
      {/* Fixed, under the bar: its place among the wrapper's children does not matter. */}
      {open && <MenuScrim onDismiss={close} className="z-10" />}
    </div>
  );
}
